import React from 'react';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import './index.scss';
import { ExpandableDisclaimer } from '@zillow/zrs-comet';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import IconifiedList from '@zg-rentals/multibrand-ui/lib/IconifiedList';
import RadioSelected from '@zg-rentals/multibrand-ui/lib/icons/RadioSelected';
import RadioDeselected from '@zg-rentals/multibrand-ui/lib/icons/RadioDeselected';
import type { IStyledTextModel } from '@rental-manager/models/Types';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';
import Bell from '@zg-rentals/multibrand-ui/lib/icons/Bell';
import BluePrintIcon from '@assets/Blueprint.svg';
import DebouncedButton from '@common/components/DebouncedButton';
import StringConstants from '@common/constants/StringConstants';

type RentalProtectionDisclaimers = Array<IStyledTextModel>;

type Props = {
  onClose: () => void;
  onBack: () => void;
  rentalProtectionDisclaimers: RentalProtectionDisclaimers;
  getRentalProtectionDisclaimers: () => void;
  acknowledgePaymentPlan: () => Promise<any>;
};

const HowToSetupPaymentPlan: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const { rentalProtectionDisclaimers } = props;
  const [paymentPlanDocChecked, setPaymentPlanDocChecked] = React.useState<boolean>(false);

  React.useEffect(() => {
    const { getRentalProtectionDisclaimers } = props;
    getRentalProtectionDisclaimers();
  }, []);

  const onChange = () => {
    Promise.resolve().then(() => setPaymentPlanDocChecked(!paymentPlanDocChecked));
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="PAYMENT PLAN"
      onClickBack={props.onBack}
      onRequestClose={props.onClose}
      dataAutoTestId="landlord-how-to-setup-payment-plan"
    >
      <Row>
        <Col>
          <Row>
            <Col className="txtC">
              <img src={BluePrintIcon as unknown as string} alt="" className="bluePrintIcon" />
              <Text type="h4" className="zillowInsuranceSubHeading" emphasize>
                Zillow Insurance Services
              </Text>
              <Text type="h3">How to set up a payment plan</Text>
            </Col>
          </Row>
          <IconifiedList
            className="Iconified-List"
            verticalLineColor="borderLight"
            iconSize="sm"
            listItems={[
              {
                icon: <RadioSelected colorType="action" />,
                primaryContent: <Text emphasize>Step 1: Talk with your tenant and decide what to do</Text>,
                secondaryContent: (
                  <Text type="body-small">
                    Agree on what they&apos;ll pay and when. Keep in mind, the entire amount of unpaid rent has to be
                    collected by the end of the lease term. Amounts collected after the lease expires may not be
                    eligible for Rental Protection coverage.{' '}
                  </Text>
                ),
              },
              {
                icon: <RadioDeselected colorType="text-secondary" />,
                primaryContent: <Text emphasize>Step 2: Make your plan official</Text>,
                secondaryContent: (
                  <>
                    <Text type="body-small">
                      Use a document of your choice to record the payment plan you and your tenant agreed on. Looking
                      for an example?&nbsp;
                      <Link href={StringConstants.LINKS.PAYMENT_PLAN_TEMPLATE} target="_blank" type="primary">
                        Download our payment plan template
                      </Link>
                      &nbsp;
                    </Text>
                    <IconifiedList
                      className="Iconified-List-Nested"
                      verticalLineColor="borderLight"
                      iconSize="sm"
                      listItems={[
                        {
                          icon: <Bell colorType="action" size="sm" />,
                          primaryContent: (
                            <Text emphasize>The total amount to repay must be the same as the missed rent</Text>
                          ),
                          secondaryContent: (
                            <Text type="body-small">
                              If you discount or forgive part of the rent, Rental Protection coverage won&apos;t apply
                              to this amount.
                            </Text>
                          ),
                        },
                      ]}
                    />
                  </>
                ),
              },
              {
                icon: <RadioDeselected colorType="text-secondary" />,
                primaryContent: <Text emphasize>Step 3: Set up payment plan installments</Text>,
                secondaryContent: (
                  <Text type="body-small">
                    For each individual payment your tenant will make as part of the payment plan, request a payment
                    through Zillow.
                  </Text>
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Checkbox
            className="Checkbox-alignment"
            checked={paymentPlanDocChecked}
            onChange={onChange}
            label="The tenant and I signed a payment plan document,
                        as required for Rental Protection coverage."
          />
        </Col>
      </Row>
      <SpacerRow size="md" />
      <Row>
        <Col>
          <ExpandableDisclaimer disclaimerContent={rentalProtectionDisclaimers} title="Legal Disclaimer" />
        </Col>
      </Row>
      <SpacerRow size="sm" />
      <Row>
        <Col>
          <DebouncedButton onClick={props.acknowledgePaymentPlan} type="primary" full disabled={!paymentPlanDocChecked}>
            Set up payment plan
          </DebouncedButton>
        </Col>
        <SpacerRow />
        <Col>
          <Button type="secondary" full onClick={props.onBack}>
            Cancel
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default HowToSetupPaymentPlan;
